// OWNED BY the meetings tab agent (created under detail/tabs/meetings/).
//
// One meeting row: event-type icon + event-color stripe (ported from
// CalendarTasksRow), title, Hebrew when-caption, other-attendee names, the stored
// message to the invitees when there is one, and the
// linked-listing chips (ALL of them, width permitting — see
// MeetingListingChips.tsx). Today's meetings get a subtle info-tinted
// highlight. Owner-only editing: non-own rows are not clickable and show the
// owner's email hint (same convention as the legacy calendar rows).

import { Box, Stack, Typography, alpha } from "@material-ui/core";
import { Event, Groups } from "@material-ui/icons";
import useAuth from "hooks/useAuth";
import { MeetingListingChips } from "./MeetingListingChips";
import {
  formatMeetingWhenLabel,
  isMeetingToday,
  isOwnCalendarEvent,
  meetingListingsOf,
  otherAttendeeNames
} from "./meetingsData";
import type { CalendarEvent, RealEstateContact } from "../../../../../../@types/sharedSchema";

const DEFAULT_EVENT_COLOR = "#1890FF"; // CalendarForm's default event color

type ContactMeetingRowProps = {
  meeting: CalendarEvent;
  contact: RealEstateContact;
  isUpcoming: boolean;
  onOpen: (meeting: CalendarEvent) => void;
};

export function ContactMeetingRow({
  meeting,
  contact,
  isUpcoming,
  onOpen
}: ContactMeetingRowProps): JSX.Element {
  const { user } = useAuth();

  const userEmail: string = user?.email ?? "";

  const isOwner = isOwnCalendarEvent(meeting, userEmail);
  const canOpen = isOwner && !!meeting.id;
  const highlightToday = isUpcoming && isMeetingToday(meeting);

  const stripeColor = meeting.backgroundColor ?? meeting.textColor ?? DEFAULT_EVENT_COLOR;
  const listings = meetingListingsOf(meeting);
  const others = otherAttendeeNames(meeting, contact);

  const whenLabel = formatMeetingWhenLabel(meeting);
  const caption = others.length ? `${whenLabel} · עם ${others.join(", ")}` : whenLabel;

  // The note the realtor wrote FOR the invitees (CalendarEvent.clientNote). This is what is
  // STORED on the meeting, not proof that it was delivered, hence the neutral label.
  const clientNote = meeting.eventType === "task" ? "" : (meeting.clientNote ?? "").trim();

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.25}
      onClick={canOpen ? () => onOpen(meeting) : undefined}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        cursor: canOpen ? "pointer" : "default",
        "&:hover": canOpen ? { bgcolor: "action.hover" } : undefined,
        ...(highlightToday && {
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.06)
        })
      }}
    >
      <Box
        sx={{
          width: 4,
          alignSelf: "stretch",
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: stripeColor
        }}
      />

      {meeting.eventType === "meeting" ? (
        <Groups fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
      ) : (
        <Event fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
      )}

      <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          variant="body2"
          noWrap
          dir="auto"
          sx={isUpcoming ? undefined : { color: "text.secondary" }}
        >
          {meeting.title}
          {!isOwner && !!meeting.userId && (
            <Typography component="span" variant="caption" sx={{ color: "text.disabled" }}>
              {" "}
              ({meeting.userId})
            </Typography>
          )}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ color: highlightToday ? "info.dark" : "text.secondary" }}
        >
          {caption}
        </Typography>
        {!!clientNote && (
          // Wraps (two lines at most) instead of `noWrap` + `title`: a hover tooltip is
          // unreadable on touch.
          <Typography
            variant="caption"
            dir="auto"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere"
            }}
          >
            הודעה למוזמנים: {clientNote}
          </Typography>
        )}
      </Stack>

      <MeetingListingChips listings={listings} />
    </Stack>
  );
}
